/**
 * Structural safety checks for uploaded PDFs.
 *
 * Page count and byte size stop *honest* mistakes (someone drops the whole
 * closing package). They do nothing about a deliberately malformed file: a
 * 40 KB PDF can carry a million objects, dictionaries nested thousands deep,
 * or a decompression bomb, and any of those can pin a parser long before the
 * page count is ever read.
 *
 * So before pdf.js or the AI ever touches a document we do a single cheap
 * pass over the raw bytes and reject anything structurally hostile. These are
 * heuristics on purpose — they run in constant memory, never execute or
 * decompress anything, and are tuned so a real title commitment (a few dozen
 * pages of scanned text) never trips them.
 *
 * Pure and dependency-free, so the same checks run in the browser before
 * upload and again on the server after decoding the payload.
 */

export const PDF_SAFETY_LIMITS = {
  /** Indirect objects (`12 0 obj`). Real title commitments are in the hundreds. */
  maxObjects: 50_000,
  /** Content streams. */
  maxStreams: 20_000,
  /** Dictionary nesting depth (`<< ... >>`). Deep nesting is a parser bomb. */
  maxNestingDepth: 64,
  /** Attached files carried inside the PDF. */
  maxEmbeddedFiles: 8,
  /** How far in we look for the `%PDF-` signature, per spec tolerance. */
  headerSearchBytes: 1024,
  /** How far back we look for the `%%EOF` trailer. */
  trailerSearchBytes: 4096,
} as const;

export type PdfSafetyCode =
  | "not_pdf"
  | "truncated"
  | "encrypted"
  | "too_many_objects"
  | "too_many_streams"
  | "too_deeply_nested"
  | "too_many_embedded_files"
  | "active_content";

export type PdfSafetyIssue = {
  code: PdfSafetyCode;
  /** Plain-English reason shown to the user. */
  message: string;
  /** Short label for the rejection card. */
  title: string;
};

export type PdfSafetyReport = {
  /** Bytes scanned. */
  byteSize: number;
  objects: number;
  streams: number;
  maxNestingDepth: number;
  embeddedFiles: number;
  encrypted: boolean;
  hasActiveContent: boolean;
  /** First blocking problem, or null when the structure looks sane. */
  issue: PdfSafetyIssue | null;
};

/** Byte-wise indexOf for an ASCII needle — avoids materializing a huge string. */
function indexOfAscii(bytes: Uint8Array, needle: string, from = 0, to = bytes.length): number {
  const n = needle.length;
  if (n === 0) return from;
  const first = needle.charCodeAt(0);
  const limit = Math.min(to, bytes.length) - n;
  for (let i = from; i <= limit; i += 1) {
    if (bytes[i] !== first) continue;
    let j = 1;
    while (j < n && bytes[i + j] === needle.charCodeAt(j)) j += 1;
    if (j === n) return i;
  }
  return -1;
}

function isDigit(byte: number | undefined): boolean {
  return byte !== undefined && byte >= 0x30 && byte <= 0x39;
}

function isSpace(byte: number | undefined): boolean {
  return byte === 0x20 || byte === 0x0a || byte === 0x0d || byte === 0x09;
}

/** Does `bytes` match the ASCII `word` at `i`? */
function matchesAt(bytes: Uint8Array, i: number, word: string): boolean {
  for (let j = 0; j < word.length; j += 1) {
    if (bytes[i + j] !== word.charCodeAt(j)) return false;
  }
  return true;
}

/**
 * Single pass over the file, counting structure and tracking dictionary depth.
 *
 * String literals are skipped so that parentheses/angle brackets inside scanned
 * text can't inflate the depth counter, and escapes inside them are honored.
 */
function scan(bytes: Uint8Array) {
  let objects = 0;
  let streams = 0;
  let embeddedFiles = 0;
  let depth = 0;
  let maxDepth = 0;
  let hasActiveContent = false;
  let i = 0;

  while (i < bytes.length) {
    const b = bytes[i];

    // ( ... ) literal string — skip wholesale, honoring backslash escapes.
    if (b === 0x28) {
      let nest = 1;
      i += 1;
      while (i < bytes.length && nest > 0) {
        const c = bytes[i];
        if (c === 0x5c)
          i += 1; // escape: skip next byte
        else if (c === 0x28) nest += 1;
        else if (c === 0x29) nest -= 1;
        i += 1;
      }
      continue;
    }

    if (b === 0x3c && bytes[i + 1] === 0x3c) {
      depth += 1;
      if (depth > maxDepth) maxDepth = depth;
      i += 2;
      continue;
    }
    if (b === 0x3e && bytes[i + 1] === 0x3e) {
      if (depth > 0) depth -= 1;
      i += 2;
      continue;
    }

    if (b === 0x6f && matchesAt(bytes, i, "obj")) {
      // `obj` only counts when preceded by `<num> <gen> ` — the real syntax.
      let k = i - 1;
      while (isSpace(bytes[k])) k -= 1;
      if (isDigit(bytes[k])) {
        while (isDigit(bytes[k])) k -= 1;
        while (isSpace(bytes[k])) k -= 1;
        if (isDigit(bytes[k])) objects += 1;
      }
      i += 3;
      continue;
    }

    // `stream` opens a body; ignore the matching `endstream`.
    if (b === 0x73 && matchesAt(bytes, i, "stream")) {
      if (!(i >= 3 && matchesAt(bytes, i - 3, "end"))) streams += 1;
      i += 6;
      continue;
    }

    if (b === 0x2f) {
      // A name token: the ones we care about are all active-content markers.
      if (matchesAt(bytes, i, "/EmbeddedFile")) {
        embeddedFiles += 1;
        i += 13;
        continue;
      }
      if (
        matchesAt(bytes, i, "/JavaScript") ||
        matchesAt(bytes, i, "/Launch") ||
        matchesAt(bytes, i, "/OpenAction") ||
        matchesAt(bytes, i, "/RichMedia") ||
        matchesAt(bytes, i, "/AA")
      ) {
        hasActiveContent = true;
      }
    }

    i += 1;
  }

  return { objects, streams, embeddedFiles, maxNestingDepth: maxDepth, hasActiveContent };
}

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

/**
 * Inspect raw PDF bytes. Returns counts plus the first blocking issue, so the
 * UI can explain exactly what tripped instead of a generic "invalid file".
 */
export function inspectPdfBytes(bytes: Uint8Array): PdfSafetyReport {
  const headerAt = indexOfAscii(
    bytes,
    "%PDF-",
    0,
    Math.min(bytes.length, PDF_SAFETY_LIMITS.headerSearchBytes),
  );

  const base = {
    byteSize: bytes.length,
    objects: 0,
    streams: 0,
    maxNestingDepth: 0,
    embeddedFiles: 0,
    encrypted: false,
    hasActiveContent: false,
  };

  if (headerAt < 0) {
    return {
      ...base,
      issue: {
        code: "not_pdf",
        title: "Not a PDF",
        message:
          "This file isn't a PDF. It's missing the PDF file signature. If you renamed a scan or an image, export it as a real PDF and try again.",
      },
    };
  }

  const tailFrom = Math.max(0, bytes.length - PDF_SAFETY_LIMITS.trailerSearchBytes);
  if (indexOfAscii(bytes, "%%EOF", tailFrom) < 0) {
    return {
      ...base,
      issue: {
        code: "truncated",
        title: "File looks incomplete",
        message:
          "This PDF is missing its end-of-file marker, which usually means the upload or the original download was cut short. Re-download the document and upload it again.",
      },
    };
  }

  const counts = scan(bytes);
  const encrypted = indexOfAscii(bytes, "/Encrypt") >= 0;
  const report: PdfSafetyReport = { ...base, ...counts, encrypted, issue: null };

  if (encrypted) {
    report.issue = {
      code: "encrypted",
      title: "Password-protected PDF",
      message:
        "This PDF is encrypted, so its text can't be read. Open it, save or print an unprotected copy, and upload that instead.",
    };
    return report;
  }

  if (counts.objects > PDF_SAFETY_LIMITS.maxObjects) {
    report.issue = {
      code: "too_many_objects",
      title: "Document structure is too complex",
      message: `This PDF contains ${plural(counts.objects, "internal object")}. Well past the ${PDF_SAFETY_LIMITS.maxObjects.toLocaleString()} we can safely process. Upload just the title commitment pages (Schedule A and Schedule B).`,
    };
    return report;
  }

  if (counts.streams > PDF_SAFETY_LIMITS.maxStreams) {
    report.issue = {
      code: "too_many_streams",
      title: "Document structure is too complex",
      message: `This PDF contains ${plural(counts.streams, "content stream")}, past the ${PDF_SAFETY_LIMITS.maxStreams.toLocaleString()} limit. Upload just the title commitment pages instead of the full closing package.`,
    };
    return report;
  }

  if (counts.maxNestingDepth > PDF_SAFETY_LIMITS.maxNestingDepth) {
    report.issue = {
      code: "too_deeply_nested",
      title: "Malformed PDF structure",
      message: `This PDF nests its internal structures ${counts.maxNestingDepth} levels deep (the safe limit is ${PDF_SAFETY_LIMITS.maxNestingDepth}). Files like this can hang a reader, so we don't open them. Re-export or print the document to a fresh PDF and try again.`,
    };
    return report;
  }

  if (counts.embeddedFiles > PDF_SAFETY_LIMITS.maxEmbeddedFiles) {
    report.issue = {
      code: "too_many_embedded_files",
      title: "Too many attachments",
      message: `This PDF carries ${plural(counts.embeddedFiles, "embedded attachment")}. We read title text only, and don't open attachments. Upload the title commitment on its own.`,
    };
    return report;
  }

  return report;
}

/**
 * Decode a `data:application/pdf;base64,...` URL to bytes for server-side
 * inspection. Returns null when the payload isn't a base64 PDF data URL.
 */
export function decodePdfDataUrl(dataUrl: string): Uint8Array<ArrayBuffer> | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  const header = dataUrl.slice(0, comma);
  if (!header.startsWith("data:") || !header.includes("base64")) return null;
  try {
    const binary = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Scanned PDFs carry page images but almost no text-showing operators.
 * Counting Tj/TJ operators in the raw bytes is a cheap proxy for "how much
 * text would a normal extractor get" without pulling a PDF parser into the
 * Worker runtime. Shared by the report and title analyzers so both decide
 * "scan vs. text" the same way.
 */
export const TEXT_OPERATOR_THRESHOLD = 40;

export function estimateTextOperators(fileDataUrl: string): number {
  try {
    const base64 = fileDataUrl.slice(fileDataUrl.indexOf(",") + 1);
    const binary = atob(base64.slice(0, 4_000_000));
    return (binary.match(/(?:Tj|TJ)[\s)\]]/g) ?? []).length;
  } catch {
    return TEXT_OPERATOR_THRESHOLD; // unknown. Assume text-based
  }
}
